/**
 * Attribute-form logic shared by the block inspector panel (canvas) and the
 * block settings popover (document surface): general/css segment split,
 * slot-count routing and the store write path.
 */

import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import { isSharedBlock, markerId } from '../types';
import { useBuilderRegistry } from '../useBuilderRegistry';
import { useBuilderStore } from '../useBuilderStore';
import type { BlockNode } from '../types';
import type { TypeAttribute } from '../useBuilderRegistry';
import type { MutationOrigin } from '../useBuilderStore';

export interface BlockAttributes {
    generalAttributes: ComputedRef<TypeAttribute[]>;
    cssAttributes: ComputedRef<TypeAttribute[]>;
    /** The attribute driving the container's slot count (Columns cols). */
    slotCountAttribute: ComputedRef<string | null>;
    updateAttribute(slug: string, value: unknown): void;
}

export function useBlockAttributes(
    block: () => BlockNode | null,
    origin?: MutationOrigin,
): BlockAttributes {
    const store = useBuilderStore();
    const registry = useBuilderRegistry();

    const attributes = computed<TypeAttribute[]>(() => {
        const current = block();

        if (!current || isSharedBlock(current)) {
            return [];
        }

        return registry.typeOf(current.type)?.attributes ?? [];
    });

    const slotCountAttribute = computed<string | null>(() => {
        const current = block();

        if (!current || isSharedBlock(current)) {
            return null;
        }

        const slots = registry.typeOf(current.type)?.container?.slots;

        return slots && slots.mode === 'attribute' ? slots.attribute : null;
    });

    return {
        generalAttributes: computed(() => attributes.value.filter((attribute) => attribute.segment !== 'css')),
        cssAttributes: computed(() => attributes.value.filter((attribute) => attribute.segment === 'css')),
        slotCountAttribute,
        updateAttribute(slug, value) {
            const id = markerId(block() ?? ({} as BlockNode));

            if (id === null) {
                return;
            }

            if (slug === slotCountAttribute.value) {
                // slot-count change folds orphan cells too — one undo step
                store.setSlotCount(id, slug, Number(value));
                return;
            }

            store.updateValue(id, slug, value, origin);
        },
    };
}
